import { configApi } from './config-api';
import { useAuthStore } from '@/store/useAuthStore';

const LS_PREFIX = 'bsc.config.keys.';
export const CONFIG_KEYS_UPDATED_EVENT = 'bsc-config-keys-updated';

type ConfigKeyDict = Record<string, string>;

type MemoryCache = {
  tenantId: string | null;
  promise: Promise<ConfigKeyDict> | null;
  value: ConfigKeyDict | null;
};

const memory: MemoryCache = {
  tenantId: null,
  promise: null,
  value: null,
};

function resolveTenantId(explicit?: string | null): string {
  if (explicit) return explicit;
  try {
    return useAuthStore.getState().tenantID || '';
  } catch {
    return '';
  }
}

function lsKey(tenantId: string): string {
  return `${LS_PREFIX}${tenantId || '_'}`;
}

function readFromLocalStorage(tenantId: string): ConfigKeyDict | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(lsKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as ConfigKeyDict;
  } catch {
    return null;
  }
}

function writeToLocalStorage(tenantId: string, dict: ConfigKeyDict): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(lsKey(tenantId), JSON.stringify(dict));
    window.dispatchEvent(new CustomEvent(CONFIG_KEYS_UPDATED_EVENT, { detail: { tenantId } }));
  } catch {
    // ignore quota / private mode
  }
}

function removeFromLocalStorage(tenantId?: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (tenantId) {
      localStorage.removeItem(lsKey(tenantId));
      return;
    }
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(LS_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

async function fetchConfigKeysFromApi(): Promise<ConfigKeyDict> {
  const res = (await configApi.getKeys()) as unknown as {
    data?: { data?: unknown } | ConfigKeyDict;
  };
  const level1 = res?.data;
  if (level1 && typeof level1 === 'object' && 'data' in level1) {
    const nested = (level1 as { data?: unknown }).data;
    return (nested && typeof nested === 'object' ? nested : level1) as ConfigKeyDict;
  }
  return (level1 ?? {}) as ConfigKeyDict;
}

/**
 * Lấy config keys (flat dict: key -> content), cache memory + localStorage.
 * Auto-restore nếu memory trống.
 */
export async function getConfigKeys(force = false, tenantId?: string | null): Promise<ConfigKeyDict> {
  const tid = resolveTenantId(tenantId);

  // Cache hit
  if (!force && memory.value && memory.tenantId === tid) {
    return memory.value;
  }

  // DEDUP: nếu đang fetch cùng tenant, reuse promise
  if (!force && memory.promise && memory.tenantId === tid) {
    return memory.promise;
  }

  // Auto-restore: nếu memory trống, thử restore từ localStorage
  if (!force && !memory.value && tid) {
    const fromLs = readFromLocalStorage(tid);
    if (fromLs && Object.keys(fromLs).length) {
      memory.tenantId = tid;
      memory.value = fromLs;
      return fromLs;
    }
  }

  // Memory trống hoàn toàn hoặc force -> gọi API
  memory.tenantId = tid;
  memory.promise = fetchConfigKeysFromApi()
    .then((dict) => {
      memory.value = dict;
      writeToLocalStorage(tid, dict);
      return dict;
    })
    .catch((err) => {
      memory.promise = null;
      throw err;
    });

  return memory.promise;
}

/** Lấy content trực tiếp từ dict (flat structure: key -> content). */
export function getConfigKeyContentFromDict(
  dict: ConfigKeyDict | null | undefined,
  key: string,
  defaultValue?: string,
): string | undefined {
  if (!dict) return defaultValue;
  return dict[key] ?? defaultValue;
}

export async function getConfigKeyContent(
  key: string,
  defaultValue?: string,
  options?: { force?: boolean; tenantId?: string | null },
): Promise<string | undefined> {
  try {
    const dict = await getConfigKeys(options?.force ?? false, options?.tenantId);
    return getConfigKeyContentFromDict(dict, key, defaultValue);
  } catch {
    return defaultValue;
  }
}

/** Xóa cache memory + localStorage (toàn bộ tenant hoặc 1 tenant). */
export function invalidateConfigKeysCache(tenantId?: string | null): void {
  const tid = tenantId ?? memory.tenantId;
  memory.promise = null;
  memory.value = null;
  memory.tenantId = null;
  removeFromLocalStorage(tid);
}
